import { useState } from 'react';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';
import { Item } from '@baemingo/shopping-cart-events/dist/types';
import { useBridgeApi, useDispatch, useSelector } from './index';
import * as actions from '../actions';
import { useShoppingCart, useShoppingCartApi } from '../Components/ShoppingCartUniverse';
import { useStorage } from '../Components/Storage';

export function useMenuExpired() {
  const { push } = useHistory();
  const api = useBridgeApi();
  const shoppingCart = useShoppingCart();
  const shoppingCartApi = useShoppingCartApi();
  const dispatch = useDispatch();
  const [, updateStorage] = useStorage();
  const [isMenuExpired, setIsMenuExpired] = useState(false);
  const [expiredItems, setExpiredItems] = useState<Item[]>([]);
  const menu = useSelector((state) => state.menu);

  // Check if the menu has expired
  const checkMenuExpired = () => {
    if (!menu) {
      return false;
    }

    const timeToCheckTowards =
      shoppingCart.pickupTime && shoppingCart.pickupTime !== 'ASAP'
        ? DateTime.fromISO(shoppingCart.pickupTime)
        : DateTime.local();
    const currentDayOfWeek = timeToCheckTowards.toFormat('cccc').toUpperCase(); // Get current day of the week in uppercase
    const todaysAvailabilities = menu.availabilities.filter(
      (a) => a.dayOfWeek === currentDayOfWeek,
    );

    if (todaysAvailabilities.length === 0) {
      return false;
    }

    // ✅ true if we’re inside ANY slot
    const insideAnyTimeslots = todaysAvailabilities.some(({ timeRange }) => {
      const [sh, sm] = timeRange.start.split(':').map(Number);
      const [eh, em] = timeRange.end.split(':').map(Number);

      // anchor to the same calendar date as timeToCheckTowards
      const startTime = timeToCheckTowards.set({
        hour: sh,
        minute: sm,
        second: 0,
        millisecond: 0,
      });
      let endTime = timeToCheckTowards.set({ hour: eh, minute: em, second: 0, millisecond: 0 });

      // handle overnight windows (e.g. 23:00–01:00) and your 00:00 special case
      if (timeRange.end === '00:00') {
        endTime = startTime
          .plus({ days: 1 })
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      } else if (endTime <= startTime) {
        endTime = endTime.plus({ days: 1 });
      }

      return timeToCheckTowards >= startTime && timeToCheckTowards < endTime;
    });

    if (insideAnyTimeslots) {
      return false;
    }

    setIsMenuExpired(true);
    return true;
  };

  // Find items that are no longer in the menu
  const findExpiredItems = () => {
    if (!menu) {
      return [];
    }

    const items: Item[] = [];

    shoppingCart.items.forEach((item) => {
      const menuEntry = menu.menu.menuEntries.find((e) => item.entryId === e.id);
      if (!menuEntry) {
        items.push(item);
      }
    });

    return items;
  };

  // Check the menu then items
  const checkCartMenuExpired = () => {
    const menuExpired = checkMenuExpired();

    if (menuExpired) {
      return true;
    }

    const items = findExpiredItems();

    if (items.length > 0) {
      setExpiredItems(items);
      return true;
    }

    return false;
  };

  // Clear cart
  const clearCart = () => {
    api.vibrate('impactLight');
    shoppingCartApi.clearPromoCode();
    shoppingCartApi.clearCart();
    dispatch(actions.setCartId(null));
    updateStorage({ cartId: null });
  };

  // Callback to close item modal (menu is expired)
  const onConfirmMenuExpired = () => {
    clearCart();
    setIsMenuExpired(false);
    push('/');
  };

  // Callback to close cart modal (menu or items expired)
  const onConfirmCartMenuExpired = () => {
    if (isMenuExpired) {
      onConfirmMenuExpired();
      return;
    }

    expiredItems.forEach((item) => {
      shoppingCartApi.removeItem(item.id);
    });

    setExpiredItems([]);
  };

  return {
    checkMenuExpired,
    checkCartMenuExpired,
    isMenuExpired,
    isItemsExpired: expiredItems.length > 0,
    onConfirmMenuExpired,
    onConfirmCartMenuExpired,
    expiredItems,
  };
}
